if (!customElements.get('crafty-globo-preview')) {
  customElements.define(
    'crafty-globo-preview',
    class CraftyGloboPreview extends HTMLElement {
      static selectors = {
        style: 'input[data-field-name="Clothing Style"]',
        teeColor: 'input[data-field-name="Tee Clothing Color"]',
        crewneckColor: 'input[data-field-name="Crewneck Clothing Color"]',
        hoodieColor: 'input[data-field-name="Hoodie Clothing Color"]',
        zipHoodieColor: 'input[data-field-name="Zip Hoodie Clothing Color"]',
        placement:
          'input[data-field-name="Design Placement on Zip Hoodie"]',
        design: 'input[data-field-name="Choose Your Design"]',
      };

      static colorSelectorByStyle = {
        Tee: 'teeColor',
        Crewneck: 'crewneckColor',
        Hoodie: 'hoodieColor',
        'Zip Hoodie': 'zipHoodieColor',
      };

      /*
       * TODO: Add only confirmed Globo design-value aliases here.
       * Keys must be exact values emitted by Globo; values must match a key in
       * the data-design-images JSON map. No unconfirmed aliases are assumed.
       */
      static designValueAliases = {
        Addicted: 'Edikted',
        'Original Mountain': 'The Original Mountain',
        'Raising Kids': 'Raising Agawak Girls',
      };

      connectedCallback() {
        this.garmentImages = this.readJson('[data-garment-images]');
        this.designImages = this.readJson('[data-design-images]');
        this.garmentImage = this.querySelector('[data-preview-garment]');
        this.designImage = this.querySelector('[data-preview-design]');
        this.emptyState = this.querySelector('[data-preview-empty]');
        this.status = this.querySelector('[data-preview-status]');
        this.viewControls = this.querySelector('[data-preview-views]');
        this.previewSide = 'front';
        this.updateQueued = false;

        this.handleFieldEvent = this.handleFieldEvent.bind(this);
        this.queueUpdate = this.queueUpdate.bind(this);

        this.bindPreviewControls();
        document.addEventListener('change', this.handleFieldEvent);
        document.addEventListener('input', this.handleFieldEvent);

        this.observer = new MutationObserver(this.queueUpdate);
        this.observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['checked', 'value'],
        });

        this.updatePreview();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.handleFieldEvent);
        document.removeEventListener('input', this.handleFieldEvent);
        if (this.observer) this.observer.disconnect();
      }

      readJson(selector) {
        const element = this.querySelector(selector);
        if (!element) return {};

        try {
          return JSON.parse(element.textContent);
        } catch (error) {
          return {};
        }
      }

      isGloboField(element) {
        if (!(element instanceof HTMLInputElement)) return false;
        return Object.values(CraftyGloboPreview.selectors).some((selector) =>
          element.matches(selector)
        );
      }

      handleFieldEvent(event) {
        if (this.isGloboField(event.target)) this.queueUpdate();
      }

      queueUpdate() {
        if (this.updateQueued) return;
        this.updateQueued = true;

        window.requestAnimationFrame(() => {
          this.updateQueued = false;
          this.updatePreview();
        });
      }

      selectedInputValue(selector) {
        const inputs = [...document.querySelectorAll(selector)];
        if (!inputs.length) return '';

        const checkedInput = inputs.find(
          (input) =>
            (input.type === 'radio' || input.type === 'checkbox') && input.checked
        );
        if (checkedInput) return checkedInput.value.trim();

        const valueInput = inputs.find(
          (input) =>
            input.type !== 'radio' &&
            input.type !== 'checkbox' &&
            input.value.trim() !== ''
        );
        return valueInput ? valueInput.value.trim() : '';
      }

      selectedValue(fieldKey) {
        const selector = CraftyGloboPreview.selectors[fieldKey];
        return selector ? this.selectedInputValue(selector) : '';
      }

      selectedColor(style) {
        const fieldKey = CraftyGloboPreview.colorSelectorByStyle[style];
        return fieldKey ? this.selectedValue(fieldKey) : '';
      }

      bindPreviewControls() {
        this.querySelectorAll('[data-preview-side]').forEach((button) => {
          button.addEventListener('click', () => {
            this.setPreviewSide(button.dataset.previewSide);
          });
        });
      }

      setPreviewSide(side, refresh = true) {
        if (side !== 'front' && side !== 'back') return;

        this.previewSide = side;
        this.querySelectorAll('[data-preview-side]').forEach((button) => {
          button.setAttribute(
            'aria-pressed',
            String(button.dataset.previewSide === side)
          );
        });

        if (refresh) this.updatePreview();
      }

      resolvedDesignValue(value) {
        return CraftyGloboPreview.designValueAliases[value] || value;
      }

      clearImage(image) {
        image.hidden = true;
        image.removeAttribute('src');
        image.alt = '';
      }

      updatePreview() {
        const style = this.selectedValue('style');
        const color = this.selectedColor(style);
        const isZipHoodie = style === 'Zip Hoodie';
        const placement = isZipHoodie ? this.selectedValue('placement') : '';
        const selectedDesign = this.selectedValue('design');
        const design = this.resolvedDesignValue(selectedDesign);
        const garmentEntry =
          this.garmentImages[style] && this.garmentImages[style][color];
        const garmentUrl =
          typeof garmentEntry === 'string'
            ? garmentEntry
            : garmentEntry && garmentEntry[this.previewSide];
        const designUrl = this.designImages[design];

        if (!isZipHoodie && this.previewSide !== 'front') {
          this.setPreviewSide('front', false);
        }

        this.dataset.style = style;
        this.dataset.previewSide = isZipHoodie ? this.previewSide : 'front';
        this.dataset.designPosition = '';
        if (this.viewControls) this.viewControls.hidden = !isZipHoodie;

        if (garmentUrl) {
          this.garmentImage.src = garmentUrl;
          this.garmentImage.alt = `${color} ${style}${
            isZipHoodie ? ` ${this.previewSide}` : ''
          } preview`;
          this.garmentImage.hidden = false;
          this.emptyState.hidden = true;
        } else {
          this.clearImage(this.garmentImage);
          this.emptyState.hidden = false;
        }

        let showDesign = Boolean(garmentUrl && designUrl);
        if (isZipHoodie) {
          const showOnFront =
            placement === 'Small Front Chest' ||
            placement === 'Both (front and back)';
          const showOnBack =
            placement === 'Full Back' ||
            placement === 'Both (front and back)';

          showDesign =
            showDesign &&
            ((this.previewSide === 'front' && showOnFront) ||
              (this.previewSide === 'back' && showOnBack));

          if (showDesign) {
            this.dataset.designPosition =
              this.previewSide === 'front' ? 'small-front' : 'full-back';
          }
        }

        if (showDesign) {
          this.designImage.src = designUrl;
          this.designImage.alt = `${design} design preview`;
          this.designImage.hidden = false;
        } else {
          this.clearImage(this.designImage);
        }

        if (!style || !color) {
          this.status.textContent =
            'Choose a clothing style and color to begin your preview.';
        } else if (!garmentUrl) {
          this.status.textContent =
            'A preview image is not mapped for this clothing selection yet.';
        } else if (isZipHoodie && !placement) {
          this.status.textContent =
            'Choose a Design Placement to complete the Zip Hoodie preview.';
        } else if (!selectedDesign) {
          this.status.textContent = `${color} ${style} selected. Choose a design to complete the preview.`;
        } else if (!designUrl) {
          this.status.textContent =
            'This design name is not mapped to a preview image yet.';
        } else if (isZipHoodie && !showDesign) {
          this.status.textContent = `${
            this.previewSide === 'front' ? 'Front' : 'Back'
          } view — no design for ${placement}.`;
        } else if (isZipHoodie) {
          this.status.textContent = `${design} on ${color} ${style} — ${
            this.previewSide === 'front' ? 'Front' : 'Back'
          } view, ${placement}.`;
        } else {
          this.status.textContent = `${design} on ${color} ${style}.`;
        }
      }
    }
  );
}
